/**
 * Homepage & Contact page section defaults.
 *
 * These live here as the single source of truth for BOTH:
 *   1. What the site renders when no site_settings override is present.
 *   2. What the admin UI seeds into empty forms so an admin can see the
 *      canonical copy before making tweaks.
 *
 * Icons, layouts, and animations remain hardcoded in each component - only
 * text, links, and image URLs are exposed to the CMS.
 */

export const SOLUTIONS_OVERVIEW_DEFAULT = {
  heading: "Built for your industry",
  intro:
    "Purpose-built solutions for every business in the promotional products, print, and signage industry.",
  cards: [
    {
      title: "Distributors",
      desc: "Manage stores, clients, approvals, and fulfillment with confidence at every stage.",
      link: "/solutions/distributors",
    },
    {
      title: "Suppliers",
      desc: "Scale with production planning, inventory control, and PromoStandards endpoints.",
      link: "/solutions/suppliers",
    },
    {
      title: "Decorators",
      desc: "Equip your staff with workflows and automation to deliver high-quality results on time.",
      link: "/solutions/decorators",
    },
    {
      title: "Print Shops",
      desc: "Customizable workflows to manage unlimited decoration types and keep jobs moving.",
      link: "/solutions/print-shops",
    },
    {
      title: "Sign Companies",
      desc: "Tools to handle fast-moving projects, manage approvals, and deliver exceptional signage.",
      link: "/solutions/sign-companies",
    },
    {
      title: "QuickBooks Users",
      desc: "Connections and workflows that keep financial and order data aligned.",
      link: "/solutions/quickbooks",
    },
  ],
  view_all_label: "View all solutions",
};

// 4-column feature pillar section. Icons + per-pillar palette are position-
// locked; each pillar's title/tagline and its ordered feature-item labels are
// CMS-editable (item icons are also position-locked).
export const POWER_EVERY_PART_DEFAULT = {
  eyebrow: "Everything you need to run your business",
  heading: "Power every part of your business",
  subtitle:
    "Antera brings together the tools you need to sell, operate, get paid, and grow — all in one connected platform.",
  pillars: [
    {
      title: "Sell",
      tagline: "Win more business and close faster.",
      items: [
        { label: "CRM" },
        { label: "Leads" },
        { label: "Opportunities" },
        { label: "Presentations" },
        { label: "Quotes" },
      ],
    },
    {
      title: "Operate",
      tagline: "Streamline operations and deliver on every order.",
      items: [
        { label: "Orders" },
        { label: "Artwork" },
        { label: "Production" },
        { label: "Purchase Orders" },
        { label: "Inventory" },
        { label: "Shipping" },
      ],
    },
    {
      title: "Get Paid",
      tagline: "Simplify billing and improve cash flow.",
      items: [
        { label: "Invoices" },
        { label: "Payments" },
        { label: "Accounting" },
        { label: "Commissions" },
      ],
    },
    {
      title: "Grow",
      tagline: "Build stronger relationships and drive smarter growth.",
      items: [
        { label: "Webstores" },
        { label: "Customer Portal" },
        { label: "Reports" },
        { label: "Automation" },
        { label: "AI" },
      ],
    },
  ],
  cta_title: "One Connected Platform.",
  cta_subtitle: "All the tools. All the data. All in sync.",
  cta_label: "Explore All Features",
  cta_href: "/solutions/advance",
};

// 6-step "Get Started Now" onboarding timeline + 5-benefit strip + trust line.
// Step icons and per-step colour tone stay position-locked to slots.
export const GET_STARTED_NOW_DEFAULT = {
  eyebrow: "Getting started is easy",
  heading_lead: "Get Started",
  heading_trail: "Now",
  subtitle:
    "Our proven onboarding process gets you up and running quickly — with a team that's with you every step of the way.",
  steps: [
    { title: "Schedule Demo",     desc: "See Antera in action with a personalized demo tailored to your business." },
    { title: "Implementation",    desc: "Your dedicated team works with you to plan, configure, set up LMS, and prepare for success." },
    { title: "Training",          desc: "We train your team with role-based sessions and hands-on learning." },
    { title: "Data Import",       desc: "We securely import your customers, products, historical data, and open orders." },
    { title: "Go Live",           desc: "Go live with confidence. We validate everything so you're ready to operate." },
    { title: "Customer Success",  desc: "We stay with you long term — providing support, guidance, and ongoing optimization." },
  ],
  benefits: [
    { title: "Proven Process",       desc: "Thousands of successful implementations." },
    { title: "Expert Team",          desc: "Industry experts who understand your business." },
    { title: "Fast Time to Value",   desc: "Most customers go live in just a few weeks." },
    { title: "Measurable Results",   desc: "Start seeing efficiency gains quickly." },
    { title: "We've Got Your Back",  desc: "Unlimited support from a team that cares." },
  ],
  trust_line: "We make it simple. You stay in control. We ensure your success.",
};

export const WHY_ANTERA_DEFAULT = {
  eyebrow: "Why Antera",
  heading_lead: "Built for Today.",
  heading_highlight: "Engineered for Tomorrow.",
  intro:
    "Antera isn't just another ERP platform. We continuously invest in innovation, automation, and customer-driven development to ensure your business stays ahead of the competition. Every enhancement is designed to eliminate manual work, improve visibility, and help your organization scale with confidence.",
  cards: [
    {
      title: "Advanced Technology",
      body: "Modern cloud architecture built specifically for the promotional products, print, and signage industries. From real-time supplier integrations and workflow automation to enterprise-grade security, Antera delivers the speed, reliability, and scalability growing businesses demand.",
    },
    {
      title: "Continuous Innovation",
      body: "Our development team delivers continuous enhancements, new features, performance improvements, and customer-requested functionality throughout the year, ensuring your investment becomes more valuable over time.",
    },
    {
      title: "AI-Enhanced Productivity",
      body: "Artificial intelligence is woven throughout the Antera platform to help your team work smarter. Generate insights faster, automate repetitive tasks, surface critical business information, and make better decisions with AI-powered reporting and intelligent workflows.",
    },
    {
      title: "The Industry's Best Presentation Builder",
      body: "Create professional, branded product presentations in minutes. Showcase promotional products, print, and signage with stunning layouts, customer-specific pricing, personalized branding, comments, approvals, and online collaboration-all designed to help your team win more business.",
    },
  ],
  trust_bullets: [
    "Built for the promotional products industry",
    "Celebrating 20 Years",
    "Trusted by leading distributors and suppliers",
  ],
  cta_label: "Request Demo",
  cta_href: "/contact",
};

export const PRODUCTS_SHOWCASE_DEFAULT = {
  heading: "Our Products",
  intro:
    "A comprehensive suite designed to manage every aspect of your promotional products and print business.",
  cards: [
    {
      title: "Advance ERP",
      desc: "Complete ERP and CRM software for sales, purchasing, production, inventory, accounting, fulfillment, reporting, and customer management.",
      link: "/advance",
    },
    {
      title: "Aether",
      desc: "Corporate webstores with budgets, approvals, SSO, inventory synchronization, and seamless ERP integration.",
      link: "/aether",
    },
    {
      title: "Integrations",
      desc: "Connect suppliers, accounting platforms, payment processors, tax engines, shipping providers, and business applications.",
      link: "/integrations",
    },
  ],
  image_url: "/api/media/d3793caf-5a31-47b3-b3ea-a283f5106340",
};

export const FEATURES_GRID_DEFAULT = {
  eyebrow: "One Connected Workflow",
  heading: "Every Department. Every Step. One Source of Truth.",
  intro:
    "From first contact to final payment, Advance connects every part of your business so data, people, and processes stay in sync.",
  steps: [
    { title: "CRM",           desc: "Manage customers and relationships" },
    { title: "Presentations", desc: "Create and share branded presentations" },
    { title: "Quotes",        desc: "Build accurate quotes with pricing and margin" },
    { title: "Orders",        desc: "Convert quotes to orders seamlessly" },
    { title: "Artwork",       desc: "Send, proof, and approve artwork" },
    { title: "Production",    desc: "Schedule and manage the production floor" },
    { title: "Shipping",      desc: "Ship, track, and deliver with real-time updates" },
    { title: "Invoices",      desc: "Bill accurately and get paid faster" },
    { title: "Accounting",    desc: "Sync to accounting and reconcile easily" },
    { title: "AI Insights",   desc: "Gain insights and automate decisions" },
  ],
  benefits: [
    { title: "One Database",       desc: "All data connected and always up to date." },
    { title: "No Duplicate Entry", desc: "Enter once, it flows everywhere." },
    { title: "Fewer Errors",       desc: "Automation and validation keep you accurate." },
    { title: "Save Time",          desc: "Automate tasks and accelerate workflows." },
    { title: "Better Insights",    desc: "Real-time data to drive smarter decisions." },
  ],
  cta_label: "Explore All Features",
  cta_href: "/features",
};

export const SUCCESS_STORY_DEFAULT = {
  badge: "Real Customer Results",
  headline_lead: "How Logotology",
  headline_highlight: "achieved",
  achievements: [
    "40% increase in manufacturing output without adding headcount.",
    "50% reduction in time from order receipt to production.",
    "20% reduction in accounting workload - manual payment verification and reconciliation eliminated.",
  ],
  author_name: "Monica Scott",
  author_title: "Owner, Logotology",
  author_avatar: "/api/media/13f39edc-658e-529d-b412-c02176503530",
  main_image: "/api/media/1355202d-bf4e-4dfa-9091-4d99e6e80c8a",
  main_image_alt:
    "Tajima industrial cap embroidery machine stitching a navy cap on the shop floor at Logotology",
  stat_number: "+40%",
  stat_label: "Faster Processing",
  cta_label: "Read the Full Story",
  cta_href: "/case-studies",
};

export const FINAL_CTA_DEFAULT = {
  heading: "Scale with Confidence",
  intro:
    "Discover how Antera manages your business from first quote to final invoice.",
  primary_cta_label: "Request Demo",
  primary_cta_href: "/contact",
  secondary_cta_label: "Why Antera?",
  secondary_cta_href: "/why",
};

export const CTA_BANNER_DARK_DEFAULT = {
  heading: "Ready to streamline your operations?",
  intro:
    "Discover how Antera manages your business from first quote to final invoice.",
  primary_cta_label: "Request Demo",
  primary_cta_href: "/contact",
  secondary_cta_label: "Contact Sales",
  secondary_cta_href: "/contact",
};

export const CTA_BANNER_LIGHT_DEFAULT = {
  heading: "See Antera in action",
  intro:
    "Schedule a personalized demo and discover how Antera can transform your business.",
  cta_label: "Schedule a Demo",
  cta_href: "/contact",
};

export const CONTACT_PAGE_DEFAULT = {
  hero_lead: "Request a",
  hero_highlight: "Demo",
  intro:
    "See how Antera can transform your business. Fill out the form and our team will schedule a personalized demo tailored to your needs.",
  success_title: "Thank you!",
  success_message:
    "Your demo request has been submitted. Our team will be in touch shortly to schedule your personalized demo.",
};

// Canonical fallback used by ContactPage + Footer when site_settings.contact_info
// is missing or partial. Kept here so both places stay in sync.
export const CONTACT_INFO_DEFAULT = {
  email: "sales@anterasoftware.com",
  phone: "+1-214-556-8040",
  address: "5301 Headquarters Dr, Ste #350, Plano, TX 75024",
};

// Optional spotlight card that renders inside the homepage Hero, between the
// text column and the dashboard image. Turn `enabled` off to hide it entirely.
// DEPRECATED (Feb 2026): superseded by the full-width PRESENTATION_FEATURE
// section below. Kept here so existing overrides don't crash mergeSection().
export const PRESENTATION_SPOTLIGHT_DEFAULT = {
  enabled: false,
  eyebrow: "Industry Leading",
  title: "Best Presentation Module in the Industry",
  body: "Create stunning, interactive presentations that win more business.",
  link_label: "See Best Presentation in Action",
  link_href: "/features/presentations",
};

// Full-width "Presentation Builder" featured section, sits directly under the
// hero. Renders the copy on the left and a stylised laptop mock on the right.
// Slide image + all copy fields are CMS-editable.
export const PRESENTATION_FEATURE_DEFAULT = {
  enabled: true,
  eyebrow: "FEATURED",
  stars: 5,
  heading_line1: "The Industry's Best",
  heading_line2: "Presentation Builder",
  subtitle: "Create stunning branded presentations that close more business.",
  bullets: [
    "Interactive presentations",
    "Customer comments",
    "Online approvals",
    "Real-time collaboration",
  ],
  cta_label: "Watch Demo",
  // Video shown in the "Watch Demo" modal. This is the Presentation Builder
  // demo video, hosted by Antera in S3. Do NOT change to the Antera Academy
  // intro video - that one belongs on /academy, not on the homepage.
  video_url:
    "https://s3.us-east-1.amazonaws.com/images.anterasoftware.com/The+best+in+the+business.mp4",
  video_poster: "",
  // Deep-link (below the "Watch Demo" button, secondary link).
  deep_link_label: "Explore the Presentation Builder",
  deep_link_href: "/features/presentations",
  // The slide image inside the laptop frame. Defaults to the pre-cropped
  // Antera laptop composite bundled in /public - so any environment renders
  // the polished image without needing a database sync. CMS-editable.
  slide_image: "/homepage-laptop.png",
  // Floating annotation cards.
  comment_author: "Sara M.",
  comment_text: "Looks great! Can we try this in blue?",
  comment_time: "2h ago",
  comment_avatar: "",
  approval_author: "John D.",
  approval_status: "Approved",
  approval_time: "1h ago",
  approval_avatar: "",
};

/**
 * Merge a partial override object into a defaults tree.
 * Only top-level keys are merged; nested objects/arrays inside are replaced
 * atomically (matches our PATCH /admin/site-settings semantics).
 */
export function mergeSection(defaults, override) {
  if (!override || typeof override !== "object") return defaults;
  const out = { ...defaults };
  for (const key of Object.keys(defaults)) {
    const v = override[key];
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(defaults[key])) {
      out[key] = Array.isArray(v) && v.length > 0 ? v : defaults[key];
    } else {
      out[key] = v;
    }
  }
  return out;
}
